import { ChartCurveClockIcon, ChartPieDetailIcon } from '@eleveo/icons-react';
import { Icon as ZoomIcon } from '@zoom/components';
import React, { useEffect } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Permissions, PermissionsType } from '../../../app/AuthorizationProvider';
import { usePushNotifications } from '../../../app/push/PushNotificationProvider';
import * as routes from '../../../router/routes';
import { compose } from '../../../utils/compose';
import Icon from '../../Icon';
import { Nav } from './Nav';
import { NavGroup } from './NavGroup';
import { addMenuPathsWithPermissions } from './navUtils/addMenuPaths';
import { ICON_FILL_COLOR, ICON_SIZE } from './navUtils/constants';

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface INavItem {
  requiredPermissions?: PermissionsType[];
  title?: string;
  icon?: JSX.Element;
  submenu?: INavItem[];
  onClick?: () => void;
  text: string;
  url: string;
}

export interface WFMSideNavigationProps extends WithTranslation {
  isDrawerOpen: boolean;
}

const WFMSideNavigation: React.FC<WFMSideNavigationProps> = ({ isDrawerOpen, t }) => {
  const [openedTree, setOpenedTree] = React.useState('/');
  const { pathname } = useLocation();
  const pushNotificationProvider = usePushNotifications();

  useEffect(() => {
    setOpenedTree(pathname);
  }, [pathname]);

  const navItems: INavItem[] = [
    {
      icon: <Icon kind="scheduling" size={ICON_SIZE} color={ICON_FILL_COLOR} />,
      requiredPermissions: [Permissions.ViewAgentSchedule],
      text: t('layout.navbar.menuItems.mySchedule.title'),
      url: routes.PATH_MY_SCHEDULE,
    },
    {
      url: '/forecasting',
      icon: <Icon kind="forecasting" size={ICON_SIZE} color={ICON_FILL_COLOR} />,
      submenu: [
        {
          url: routes.PATH_FORECASTING_STUDIO,
          requiredPermissions: [Permissions.ViewForecast, Permissions.ViewQueue],
          text: t('layout.navbar.menuItems.forecasting.submenu.studio.title'),
        },
      ],
      text: t('layout.navbar.menuItems.forecasting.title'),
    },
    {
      url: '/scheduling',
      icon: <Icon kind="scheduling" size={ICON_SIZE} color={ICON_FILL_COLOR} />,
      submenu: [
        {
          url: routes.PATH_SCHEDULING_STUDIO,
          requiredPermissions: [Permissions.ViewSchedule, Permissions.ViewQueue],
          text: t('layout.navbar.menuItems.scheduling.submenu.studio.title'),
        },
        {
          url: routes.PATH_REQUESTS,
          requiredPermissions: [Permissions.ViewScheduleRequests],
          text: t('layout.navbar.menuItems.scheduling.submenu.requests.title'),
        },
      ],
      text: t('layout.navbar.menuItems.scheduling.title'),
    },
    {
      url: routes.PATH_SCHEDULE_ADHERENCE,
      icon: <ZoomIcon kind="checkmark-rounded" size={22} color={ICON_FILL_COLOR} />,
      submenu: [
        {
          url: routes.PATH_REAL_TIME_ADHERENCE,
          requiredPermissions: [Permissions.ViewRealTimeAdherence],
          text: t('layout.navbar.menuItems.scheduleAdherence.submenu.realTimeAdherence.title'),
          onClick: () => {
            pushNotificationProvider.subscribe('rta');
          },
        },
        {
          url: routes.PATH_SCHEDULE_ADHERENCE_HISTORY,
          requiredPermissions: [Permissions.ViewAdherenceHistory],
          text: t('layout.navbar.menuItems.scheduleAdherence.submenu.history.title'),
        },
        {
          url: routes.PATH_SCHEDULE_ADHERENCE_SETTINGS,
          requiredPermissions: [Permissions.ViewAdherenceSettings],
          text: t('layout.navbar.menuItems.scheduleAdherence.submenu.settings.title'),
        },
      ],
      text: t('layout.navbar.menuItems.scheduleAdherence.title'),
    },
    {
      url: routes.PATH_INTRADAY_MANAGEMENT,
      icon: <ChartCurveClockIcon className="text-xl text-grey-600" />,
      text: t('layout.navbar.menuItems.intraday.title'),
      onClick: () => {
        pushNotificationProvider.subscribe('intraday');
      },
      requiredPermissions: [Permissions.ViewIntraday],
    },
    {
      url: '/reports',
      icon: <ChartPieDetailIcon className="text-xl text-grey-600" />,
      submenu: [
        {
          url: routes.PATH_REPORTS_ADHERENCE,
          requiredPermissions: [Permissions.ViewReports],
          text: t('layout.navbar.menuItems.reports.submenu.adherence.title'),
        },
      ],
      text: t('layout.navbar.menuItems.reports.title'),
    },
    {
      url: '/administration',
      icon: <Icon kind="administration" size={ICON_SIZE} color={ICON_FILL_COLOR} />,
      submenu: [
        {
          url: routes.PATH_ADMINISTRATION_PEOPLE,
          requiredPermissions: [Permissions.ViewAgent, Permissions.ViewQueue],
          text: t('layout.navbar.menuItems.administration.submenu.people.title'),
        },
        {
          url: routes.PATH_ADMINISTRATION_SKILLS,
          requiredPermissions: [Permissions.ViewSkill],
          text: t('layout.navbar.menuItems.administration.submenu.skills.title'),
        },
        {
          url: routes.PATH_ADMINISTRATION_SHIFTS,
          requiredPermissions: [Permissions.ViewShiftTemplate],
          text: t('layout.navbar.menuItems.administration.submenu.shifts.title'),
        },
        {
          url: routes.PATH_ADMINISTRATION_QUEUES,
          requiredPermissions: [Permissions.ViewQueue],
          text: t('layout.navbar.menuItems.administration.submenu.queues.title'),
        },
        {
          url: routes.PATH_ADMINISTRATION_DATA_IMPORT,
          requiredPermissions: [Permissions.FileUpload],
          text: t('layout.navbar.menuItems.administration.submenu.dataImport.title'),
        },
      ],
      text: t('layout.navbar.menuItems.administration.title'),
    },
  ];

  return (
    <NavGroup isDrawerOpen={isDrawerOpen} data-test-id="wfm-side-navigation">
      <Nav
        items={addMenuPathsWithPermissions(navItems)}
        openedTree={openedTree}
        pathname={pathname}
        setOpenedTree={setOpenedTree}
      />
    </NavGroup>
  );
};

export const Navigation = compose(withTranslation())(WFMSideNavigation);
